/**
 * Tooltips. Both kinds are absolutely/fixed positioned with `pointer-events: none`
 * (ARCHITECTURE §0.12: overlays must never capture the pointer or cause reflow).
 *
 * * `Tooltip` — anchored to its trigger; shows on hover (after `delay`) and keyboard focus.
 * * `PointerTooltip` — a hover card that follows given coordinates inside a positioned
 *   container (e.g. the spectrum readout over the annotator canvas); it flips to stay
 *   inside the container.
 */
import clsx from 'clsx';
import { useEffect, useId, useLayoutEffect, useRef, useState, type ReactNode } from 'react';
import { createPortal } from 'react-dom';
import { useAnchoredPosition, type Placement } from './floating';
import { KeyHint } from './KeyHint';
import styles from './Tooltip.module.css';

export interface TooltipProps {
  content: ReactNode;
  /** Hotkey combo appended as keycaps. */
  shortcut?: string;
  placement?: Placement;
  /** Hover delay in ms (focus shows immediately). */
  delay?: number;
  disabled?: boolean;
  children: ReactNode;
  className?: string;
}

export function Tooltip({
  content,
  shortcut,
  placement = 'top',
  delay = 450,
  disabled = false,
  children,
  className,
}: TooltipProps) {
  const [open, setOpen] = useState(false);
  const anchorRef = useRef<HTMLSpanElement>(null);
  const tipRef = useRef<HTMLDivElement>(null);
  const timer = useRef<number | undefined>(undefined);
  const id = useId();
  const style = useAnchoredPosition(anchorRef, tipRef, open && !disabled, placement);

  const clear = () => window.clearTimeout(timer.current);
  const show = (after: number) => {
    clear();
    timer.current = window.setTimeout(() => setOpen(true), after);
  };
  const hide = () => {
    clear();
    setOpen(false);
  };
  useEffect(() => clear, []);

  return (
    <span
      ref={anchorRef}
      className={clsx(styles.anchor, className)}
      onPointerEnter={(e) => e.pointerType === 'mouse' && show(delay)}
      onPointerLeave={hide}
      onPointerDown={hide}
      onFocus={(e) => e.target.matches(':focus-visible') && show(0)}
      onBlur={hide}
      onKeyDown={(e) => e.key === 'Escape' && open && hide()}
    >
      {children}
      {open &&
        !disabled &&
        createPortal(
          <div ref={tipRef} id={id} role="tooltip" className={styles.tooltip} style={style}>
            <span>{content}</span>
            {shortcut && <KeyHint combo={shortcut} className={styles.shortcut} />}
          </div>,
          document.body,
        )}
    </span>
  );
}

export interface PointerTooltipProps {
  /** Pointer position relative to the positioned container (px). */
  x: number;
  y: number;
  visible: boolean;
  children: ReactNode;
  /** Distance from the pointer (px). */
  offset?: number;
  className?: string;
}

/** Hover card following the pointer inside its offset parent (never captures the pointer). */
export function PointerTooltip({ x, y, visible, children, offset = 14, className }: PointerTooltipProps) {
  const ref = useRef<HTMLDivElement>(null);
  useLayoutEffect(() => {
    const el = ref.current;
    const parent = el?.offsetParent as HTMLElement | null | undefined;
    if (!el || !parent) return;
    const flipX = x + offset + el.offsetWidth > parent.clientWidth;
    const flipY = y + offset + el.offsetHeight > parent.clientHeight;
    const left = flipX ? Math.max(0, x - offset - el.offsetWidth) : x + offset;
    const top = flipY ? Math.max(0, y - offset - el.offsetHeight) : y + offset;
    el.style.transform = `translate(${Math.round(left)}px, ${Math.round(top)}px)`;
  });
  return (
    <div
      ref={ref}
      className={clsx(styles.pointer, className)}
      style={{ visibility: visible ? 'visible' : 'hidden' }}
      aria-hidden
    >
      {children}
    </div>
  );
}
